import type { ReactNode } from 'react';
import type { LoanListItem, LoanPhase } from '@app/shared';
import {
  LOAN_PHASES, LINE_STATUSES, SBA_LOAN_STATUSES, ETRAN_LOAN_STATUSES, CLOSED_REASONS, ACTIONS,
} from '@app/shared';
import { formatDate as fmt, isPastDate as isPast } from '../dates';

// ---------------------------------------------------------------------------
// The column registry.
//
// Every column is declared once, here. The header row, the sort comparator, the
// "+ Add filter" field list, its operators, and its value picker are all derived
// from these descriptors — so adding a column, or promoting a derived field to a
// real DB column, is a single edit in this file.
//
// Deliberately decoupled from the data model: a column's `value` is just a
// function over the row object. Fields the API computes rather than stores
// (`lineStatus`, `hasOpenPackage`) filter and sort exactly like stored ones,
// with no SQL equivalent needed.
// ---------------------------------------------------------------------------

export type ColKind = 'text' | 'enum' | 'date' | 'money' | 'bool';

export type Column = {
  id: string;
  header: string;
  kind: ColKind;
  /** The comparable / filterable primitive. `null` means empty. */
  value: (r: LoanListItem) => string | number | boolean | null;
  /** Display text — powers the value picker, chip summaries, and "any field" search. */
  text?: (r: LoanListItem) => string;
  /** Canonical option order for enum columns. Drives both sort order and picker order. */
  options?: readonly string[];
  /** What the empty bucket is called in the value picker. Defaults to "(empty)". */
  emptyLabel?: string;
  /**
   * Overrides `value` when sorting. Needed where the filter key isn't what a
   * user would sort by — the assignment columns match on OID but must sort
   * alphabetically by person's name.
   */
  sortValue?: (r: LoanListItem) => string | number | boolean | null;
  /** Cell renderer. Omitted for filter-only columns (they never appear as a column). */
  render?: (r: LoanListItem) => ReactNode;
  /** Extra className for the rendered <td>. */
  cellClass?: (r: LoanListItem) => string | undefined;
  sortable?: boolean;
  filterable?: boolean;
};

const PHASE_COLORS: Record<LoanPhase, string> = {
  'Prepurchase': 'status-active',
  'Liquidation': 'status-pending',
  'Charge Off':  'status-submitted',
  'Closed':      'status-final',
};

export function fmtMoney(v: number | null) {
  if (v == null) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(v);
}

const YES_NO = ['Yes', 'No'] as const;

// ---------------------------------------------------------------------------

export const COLUMNS: Column[] = [
  {
    id: 'borrowerName',
    header: 'Borrower',
    kind: 'text',
    value: r => r.borrowerName,
    // The cell ellipsizes the longest names to keep rows one line, so carry the
    // full value in a tooltip rather than truncating it out of reach.
    render: r => <span title={r.borrowerName}>{r.borrowerName}</span>,
    cellClass: () => 'borrower-name',
  },
  {
    id: 'sbaLoanNumber',
    header: 'SBA Loan #',
    kind: 'text',
    value: r => r.sbaLoanNumber,
    render: r => r.sbaLoanNumber,
    cellClass: () => 'mono',
  },
  {
    id: 'umwsbLoanNumber',
    header: 'UMWSB #',
    kind: 'text',
    value: r => r.umwsbLoanNumber,
    render: r => r.umwsbLoanNumber ?? '—',
    cellClass: () => 'mono',
  },
  {
    id: 'phase',
    header: 'Phase',
    kind: 'enum',
    options: LOAN_PHASES,
    value: r => r.phase,
    render: r => <span className={`status-badge ${PHASE_COLORS[r.phase]}`}>{r.phase}</span>,
  },
  {
    id: 'lineStatus',
    header: 'Line Status',
    kind: 'enum',
    options: LINE_STATUSES,
    value: r => r.lineStatus,
    render: r => r.lineStatus ?? '—',
  },
  {
    id: 'sbaLoanStatus',
    header: 'Loan Status',
    kind: 'enum',
    options: SBA_LOAN_STATUSES,
    value: r => r.sbaLoanStatus,
    render: r => r.sbaLoanStatus ?? '—',
  },
  {
    id: 'purchaseDate',
    header: 'Purchase Date',
    kind: 'date',
    value: r => r.purchaseDate,
    text: r => fmt(r.purchaseDate),
    render: r => fmt(r.purchaseDate),
  },
  {
    id: 'legalBalance',
    header: 'Legal Balance',
    kind: 'money',
    value: r => r.legalBalance,
    text: r => fmtMoney(r.legalBalance),
    render: r => fmtMoney(r.legalBalance),
    cellClass: () => 'money',
  },
  // The two assignment columns filter on the OID but display and sort by name,
  // so "equals myself" can't be broken by a display-name collision or a rename
  // in Graph. `text` is what the picker lists; `value` is what it matches on.
  {
    id: 'assignedLoanAnalystName',
    header: 'Analyst',
    kind: 'enum',
    emptyLabel: 'Unassigned',
    value: r => r.assignedLoanAnalystId,
    text: r => r.assignedLoanAnalystName ?? 'Unassigned',
    sortValue: r => r.assignedLoanAnalystName,
    render: r => r.assignedLoanAnalystName ?? '—',
  },
  {
    id: 'assignedSaOfficerName',
    header: 'Special Assets Rep',
    kind: 'enum',
    emptyLabel: 'Unassigned',
    value: r => r.assignedSaOfficerId,
    text: r => r.assignedSaOfficerName ?? 'Unassigned',
    sortValue: r => r.assignedSaOfficerName,
    render: r => r.assignedSaOfficerName ?? '—',
  },
  {
    id: 'action',
    header: 'Action',
    kind: 'enum',
    options: ACTIONS,
    value: r => r.action,
    render: r => r.action ?? '—',
  },
  {
    id: 'nextActionDate',
    header: 'Next Action',
    kind: 'date',
    value: r => r.nextActionDate,
    text: r => fmt(r.nextActionDate),
    render: r => fmt(r.nextActionDate),
    cellClass: r => isPast(r.nextActionDate) ? 'overdue' : undefined,
  },

  // ---- Filter-only columns -------------------------------------------------
  // Not rendered as columns (the grid is already wide), but available in
  // "+ Add filter". These cost nothing: the fields are already on every row.
  {
    id: 'earlyDefault',
    header: 'Early Default',
    kind: 'bool',
    options: YES_NO,
    value: r => r.earlyDefault,
    text: r => r.earlyDefault ? 'Yes' : 'No',
  },
  {
    id: 'activeBkOrLitigation',
    header: 'Active BK / Litigation',
    kind: 'bool',
    options: YES_NO,
    value: r => r.activeBkOrLitigation,
    text: r => r.activeBkOrLitigation ? 'Yes' : 'No',
  },
  {
    id: 'hasOpenPackage',
    header: 'Has Open Package',
    kind: 'bool',
    options: YES_NO,
    value: r => r.hasOpenPackage,
    text: r => r.hasOpenPackage ? 'Yes' : 'No',
  },
  {
    id: 'etranLoanStatus',
    header: 'eTran Status',
    kind: 'enum',
    options: ETRAN_LOAN_STATUSES,
    value: r => r.etranLoanStatus,
  },
  {
    id: 'closedReason',
    header: 'Closed Reason',
    kind: 'enum',
    options: CLOSED_REASONS,
    value: r => r.closedReason,
  },
  {
    id: 'loanType',
    header: 'Loan Type',
    kind: 'enum',
    value: r => r.loanType,
  },
  {
    id: 'stateLocated',
    header: 'State',
    kind: 'enum',
    value: r => r.stateLocated,
  },
  {
    id: 'createdAt',
    header: 'Created',
    kind: 'date',
    value: r => r.createdAt,
    text: r => fmt(r.createdAt),
  },
  {
    id: 'updatedAt',
    header: 'Updated',
    kind: 'date',
    value: r => r.updatedAt,
    text: r => fmt(r.updatedAt),
  },
];

const byId = new Map(COLUMNS.map(c => [c.id, c]));

export function col(id: string): Column {
  const c = byId.get(id);
  if (!c) throw new Error(`Unknown column: ${id}`);
  return c;
}

export function cols(ids: readonly string[]): Column[] {
  return ids.map(col);
}

/** Display text for a cell, used by value pickers, chips and the any-field search. */
export function cellText(c: Column, r: LoanListItem): string {
  if (c.text) return c.text(r);
  const v = c.value(r);
  return v == null ? '' : String(v);
}

// Which columns each table shows, in order. The analyst grid also renders a
// leading expand chevron and trailing Flags/Actions cells, which are fixed
// furniture rather than data columns and stay hand-written in the page.
// Ordered by what an analyst reads first: who the loan is, where it stands, how
// to identify it, and what's next — then the supporting detail. The left-hand
// columns are the ones that must survive a horizontal scroll.
export const ANALYST_COLUMN_IDS = [
  'borrowerName', 'phase', 'lineStatus', 'sbaLoanStatus',
  'sbaLoanNumber', 'umwsbLoanNumber', 'nextActionDate', 'action',
  'purchaseDate', 'legalBalance',
  'assignedSaOfficerName', 'assignedLoanAnalystName',
] as const;

// One grid for everyone (#46, and #47's "same header view for all users"). Special
// Assets used to lose Loan Status, Purchase Date and Legal Balance — an exclusion that
// stopped making sense once SA started setting Loan Status themselves on the purchase
// request (#54). Kept as its own export so a future role split has somewhere to go.
export const SA_COLUMN_IDS: readonly string[] = ANALYST_COLUMN_IDS;

// What "Find a loan" searches: the three fields that identify a loan, matching
// the fields the old search form offered. Deliberately not every column — a
// loan-number lookup shouldn't also match a dollar amount or a date.
export const FIND_COLUMN_IDS = ['borrowerName', 'sbaLoanNumber', 'umwsbLoanNumber'] as const;

/** Columns offered in "+ Add filter" for a given table — its own columns first, then the filter-only extras. */
export function filterableFor(shownIds: readonly string[]): Column[] {
  const shown = new Set(shownIds);
  const extras = COLUMNS.filter(c => !c.render && c.filterable !== false);
  return [...cols(shownIds).filter(c => c.filterable !== false), ...extras.filter(c => !shown.has(c.id))];
}
